// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/components/ChannelMessages.module.css';
import {MARK_AS_READ_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {formatTimestamp} from '@app/features/messaging/utils/markdown/DateFormatter';
import {TimestampStyle} from '@app/features/messaging/utils/markdown/parser/Enums';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import Tick from '@app/features/ui/state/Tick';
import {msg} from '@lingui/core/macro';
import {Plural, Trans, useLingui} from '@lingui/react/macro';
import {CheckIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useMemo} from 'react';

const JUMP_TO_FIRST_UNREAD_MESSAGE_DESCRIPTOR = msg({
	message: 'Jump to first unread message',
	comment: 'Label in the channel and chat new messages bar.',
});
const NEW_SINCE_DESCRIPTOR = msg({
	message: '{unreadCount, number}+ new since {relativeTime}',
	comment:
		'Label in the channel and chat new messages bar, shown when the unread count is a lower bound. Preserve {unreadCount}, {relativeTime}; they are inserted by code. unreadCount is the estimated number of unread messages, relativeTime is the relative time of the oldest unread message.',
});
const NEW_MESSAGES_SINCE_DESCRIPTOR = msg({
	message: '{unreadCount, number}+ new messages since {relativeTime}',
	comment:
		'Label in the channel and chat new messages bar, shown when the unread count is a lower bound. Preserve {unreadCount}, {relativeTime}; they are inserted by code. unreadCount is the estimated number of unread messages, relativeTime is the relative time of the oldest unread message.',
});
const NEW_SINCE_2_DESCRIPTOR = msg({
	message: '{unreadCount, plural, one {# new} other {# new}} since {relativeTime}',
	comment:
		'Label in the channel and chat new messages bar. Preserve {unreadCount}, {relativeTime}; they are inserted by code.',
});
const MARK_READ_DESCRIPTOR = msg({
	message: 'Mark read',
	comment: 'Button or menu action label in the channel and chat new messages bar. Keep it concise.',
});
export const NewMessagesBar = observer(
	({
		unreadCount,
		oldestUnreadMessageTimestamp,
		isEstimated,
		onJumpToOldestUnread,
		onJumpToNewMessages,
	}: {
		unreadCount: number;
		oldestUnreadMessageTimestamp: number;
		isEstimated: boolean;
		onJumpToOldestUnread: () => void;
		onJumpToNewMessages: () => void;
	}) => {
		const {i18n} = useLingui();
		const isMobile = MobileLayout.isMobileLayout();
		const tick = Tick.nowSecond;
		const relativeTime = useMemo(() => {
			return oldestUnreadMessageTimestamp > 0
				? formatTimestamp(oldestUnreadMessageTimestamp, TimestampStyle.RelativeTime, i18n)
				: '';
		}, [tick, oldestUnreadMessageTimestamp, i18n.locale]);
		return (
			<div className={styles.newMessagesBarBanner} data-flx="channel.new-messages-bar.new-messages-bar">
				<button
					type="button"
					className={styles.newMessagesBarBannerText}
					onClick={onJumpToOldestUnread}
					aria-label={i18n._(JUMP_TO_FIRST_UNREAD_MESSAGE_DESCRIPTOR)}
					data-flx="channel.new-messages-bar.new-messages-bar-text.jump-to-oldest-unread.button"
				>
					{isEstimated ? (
						isMobile ? (
							i18n._(NEW_SINCE_DESCRIPTOR, {unreadCount, relativeTime})
						) : (
							i18n._(NEW_MESSAGES_SINCE_DESCRIPTOR, {unreadCount, relativeTime})
						)
					) : isMobile ? (
						i18n._(NEW_SINCE_2_DESCRIPTOR, {unreadCount, relativeTime})
					) : (
						<Trans>
							<Plural
								value={unreadCount}
								one="# new message"
								other="# new messages"
								data-flx="channel.new-messages-bar.plural"
							/>{' '}
							since {relativeTime}
						</Trans>
					)}
				</button>
				<button
					type="button"
					className={styles.newMessagesBarBannerAction}
					onClick={onJumpToNewMessages}
					data-flx="channel.new-messages-bar.new-messages-bar-action.jump-to-new-messages.button"
				>
					<span data-flx="channel.new-messages-bar.span">
						{isMobile ? i18n._(MARK_READ_DESCRIPTOR) : i18n._(MARK_AS_READ_DESCRIPTOR)}
					</span>
					<CheckIcon weight="bold" size={16} data-flx="channel.new-messages-bar.check-icon" />
				</button>
			</div>
		);
	},
);
